import { SupabaseClient } from '@supabase/supabase-js';

/**
 * Removes a tag from a company by deleting the entry from the tags_map table
 * @param supabase The Supabase client instance
 * @param tagMappingId The ID of the tag mapping to remove
 * @param slug The company slug
 * @returns A promise that resolves to the result of the deletion operation
 */
export const removeTagFromCompany = async (
  supabase: SupabaseClient,
  tagMappingId: string,
  slug: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log(`🗑️ [tagUtils] Removing tag mapping with ID: ${tagMappingId} for company: ${slug}`);

    // First, get the company ID from the slug
    const { data: companyData, error: companyError } = await supabase
      .from('companies')
      .select('id')
      .eq('slug', slug)
      .single();

    if (companyError) {
      console.error(`❌ [tagUtils] Error finding company with slug ${slug}: ${companyError.message}`);
      return { success: false, error: `Company not found: ${companyError.message}` };
    }

    const companyId = companyData.id;

    // Delete the tag mapping from the tags_map table
    // First try with 'id' as the column name (if tagMappingId is the mapping ID)
    const { error } = await supabase
      .from('tags_map')
      .delete()
      .eq('id', tagMappingId)
      .eq('company_id', companyId);

    if (error) {
      console.error(`❌ [tagUtils] Error removing tag mapping: ${error.message}`);
      return { success: false, error: error.message };
    }

    console.log(`✅ [tagUtils] Successfully removed tag mapping with ID: ${tagMappingId} for company: ${slug}`);
    return { success: true };
  } catch (error: any) {
    console.error(`❌ [tagUtils] Exception while removing tag mapping: ${error.message}`);
    return { success: false, error: error.message || 'An unknown error occurred' };
  }
};
